/**
 * Utility functions for social media platform detection and validation
 */

import {
  FACEBOOK, INSTAGRAM, LINKEDIN, X, YOUTUBE, WHATSAPP, WEBSITE, OTHER,
  FACEBOOK_DISPLAY, INSTAGRAM_DISPLAY, LINKEDIN_DISPLAY, X_DISPLAY,
  YOUTUBE_DISPLAY, WHATSAPP_DISPLAY, WEBSITE_DISPLAY, OTHER_DISPLAY,
  FACEBOOK_ICON, INSTAGRAM_ICON, LINKEDIN_ICON, X_ICON,
  YOUTUBE_ICON, WHATSAPP_ICON, WEBSITE_ICON, OTHER_ICON,
  FACEBOOK_COLOR, INSTAGRAM_COLOR, LINKEDIN_COLOR, X_COLOR,
  YOUTUBE_COLOR, WHATSAPP_COLOR, WEBSITE_COLOR, OTHER_COLOR,
  FACEBOOK_URL_PATTERN, FACEBOOK_URL_PATTERN_ALT,
  INSTAGRAM_URL_PATTERN, INSTAGRAM_URL_PATTERN_ALT,
  LINKEDIN_URL_PATTERN, LINKEDIN_URL_PATTERN_ALT,
  X_URL_PATTERN,
  YOUTUBE_URL_PATTERN, YOUTUBE_URL_PATTERN_ALT,
  WHATSAPP_URL_PATTERN, WHATSAPP_URL_PATTERN_ALT,
  SUPPORTED_PLATFORMS
} from '@/js/constants/socialMediaPlatforms'

/**
 * Detects the social media platform from a URL
 * @param {string} url - the URL to analyze
 * @returns {string} the detected platform or "other" if not recognized
 */
export function detectPlatformFromUrl(url) {
  if (!url || !url.trim()) {
    return OTHER
  }
  
  const lowerUrl = url.toLowerCase()
  
  if (lowerUrl.includes(FACEBOOK_URL_PATTERN) || lowerUrl.includes(FACEBOOK_URL_PATTERN_ALT)) {
    return FACEBOOK
  } else if (lowerUrl.includes(INSTAGRAM_URL_PATTERN) || lowerUrl.includes(INSTAGRAM_URL_PATTERN_ALT)) {
    return INSTAGRAM
  } else if (lowerUrl.includes(LINKEDIN_URL_PATTERN) || lowerUrl.includes(LINKEDIN_URL_PATTERN_ALT)) {
    return LINKEDIN
  } else if (lowerUrl.includes(X_URL_PATTERN)) {
    return X
  } else if (lowerUrl.includes(YOUTUBE_URL_PATTERN) || lowerUrl.includes(YOUTUBE_URL_PATTERN_ALT)) {
    return YOUTUBE
  } else if (lowerUrl.includes(WHATSAPP_URL_PATTERN) || lowerUrl.includes(WHATSAPP_URL_PATTERN_ALT)) {
    return WHATSAPP
  }
  
  return WEBSITE
}

/**
 * Gets the display name for a platform
 * @param {string} platform - the platform identifier
 * @returns {string} the display name
 */
export function getPlatformDisplayName(platform) {
  const names = {
    [FACEBOOK]: FACEBOOK_DISPLAY,
    [INSTAGRAM]: INSTAGRAM_DISPLAY,
    [LINKEDIN]: LINKEDIN_DISPLAY,
    [X]: X_DISPLAY,
    [YOUTUBE]: YOUTUBE_DISPLAY,
    [WHATSAPP]: WHATSAPP_DISPLAY,
    [WEBSITE]: WEBSITE_DISPLAY,
    [OTHER]: OTHER_DISPLAY
  }
  return names[platform] || platform
}

/**
 * Gets the icon name for a platform (for Material Design Icons)
 * @param {string} platform - the platform identifier
 * @returns {string} the icon name
 */
export function getPlatformIcon(platform) {
  const icons = {
    [FACEBOOK]: FACEBOOK_ICON,
    [INSTAGRAM]: INSTAGRAM_ICON,
    [LINKEDIN]: LINKEDIN_ICON,
    [X]: X_ICON,
    [YOUTUBE]: YOUTUBE_ICON,
    [WHATSAPP]: WHATSAPP_ICON,
    [WEBSITE]: WEBSITE_ICON,
    [OTHER]: OTHER_ICON
  }
  return icons[platform] || OTHER_ICON
}

/**
 * Gets the brand color for a platform
 * @param {string} platform - the platform identifier
 * @returns {string} the hex color code
 */
export function getPlatformColor(platform) {
  const colors = {
    [FACEBOOK]: FACEBOOK_COLOR,
    [INSTAGRAM]: INSTAGRAM_COLOR,
    [LINKEDIN]: LINKEDIN_COLOR,
    [X]: X_COLOR,
    [YOUTUBE]: YOUTUBE_COLOR,
    [WHATSAPP]: WHATSAPP_COLOR,
    [WEBSITE]: WEBSITE_COLOR,
    [OTHER]: OTHER_COLOR
  }
  return colors[platform] || OTHER_COLOR
}

/**
 * Validates if a platform is supported
 * @param {string} platform - the platform to validate
 * @returns {boolean} true if supported, false otherwise
 */
export function isSupportedPlatform(platform) {
  return platform && SUPPORTED_PLATFORMS.includes(platform.toLowerCase())
}

/**
 * Parses API format media links to array format
 * @param {Object|string} socialMediaSettings - social media settings from API
 * @returns {Array} array of objects with type and url properties
 */
export function parseMediaLinksFromApi(socialMediaSettings) {
  if (!socialMediaSettings) return []
  
  try {
    let settings = socialMediaSettings
    if (typeof settings === 'string') {
      settings = JSON.parse(settings)
    }
    
    if (typeof settings === 'object' && settings !== null && settings.media_links) {
      return Array.isArray(settings.media_links) ? settings.media_links : []
    }
    
    return []
  } catch (e) {
    console.warn('Failed to parse social media settings from API:', e)
    return []
  }
}

/**
 * Formats a media links array to the API format
 * @param {Array} mediaLinks - array of objects with type and url properties
 * @returns {Object} object with socialMediaSettings structure
 */
export function formatMediaLinksForApi(mediaLinks) {
  if (!Array.isArray(mediaLinks)) {
    return { socialMediaSettings: { media_links: [] } }
  }
  
  const mediaLinksArray = mediaLinks.filter(link => link.type && link.url)
  
  return {
    socialMediaSettings: {
      media_links: mediaLinksArray
    }
  }
}
